import { useState } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { EnvelopeSimple, Users, Check, Warning, Download, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function BulkSupplierInvitePage() {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [showResultDialog, setShowResultDialog] = useState(false);

  const handleBulkInvite = async () => {
    setProcessing(true);
    setShowConfirmDialog(false);
    
    try {
      const { data } = await API.post("/suppliers/bulk/invite-all");
      
      setResult(data);
      setShowResultDialog(true);
      
      toast.success(`Successfully processed ${data.summary.invitations_sent} suppliers!`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to send bulk invitations");
    }
    
    setProcessing(false);
  };

  const downloadCredentials = () => {
    if (!result || !result.invitation_details) return;
    
    const csv = [
      "Email,Name,Status,Temporary Password,Message",
      ...result.invitation_details.map(inv => 
        `${inv.email},"${inv.name}",${inv.status},"${inv.temporary_password || 'N/A'}","${inv.message}"`
      )
    ].join("\n");
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `supplier-invitations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success("Credentials downloaded as CSV");
  };

  const copyNewCredentials = () => {
    if (!result || !result.invitation_details) return;
    
    const newAccounts = result.invitation_details.filter(inv => inv.status === "new_account");
    const text = newAccounts.map(inv => 
      `${inv.name}\nEmail: ${inv.email}\nPassword: ${inv.temporary_password}\n---`
    ).join("\n\n");
    
    navigator.clipboard.writeText(text);
    toast.success("New account credentials copied to clipboard");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] flex items-center gap-2">
            <Users size={28} className="text-primary" weight="duotone" />
            Bulk Supplier Invitations
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Send supplier portal access credentials to all suppliers at once
          </p>
        </div>
      </div>

      {/* Main Action Card */}
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <div className="flex justify-center">
            <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
              <EnvelopeSimple size={40} className="text-primary" weight="duotone" />
            </div>
          </div>
          
          <div className="space-y-2">
            <h2 className="text-xl font-semibold font-[Chivo]">Invite All Suppliers</h2>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">
              This will process all suppliers in the database and:
            </p>
          </div>

          <div className="bg-muted/30 border border-border rounded-lg p-6 text-left space-y-3">
            <div className="flex items-start gap-3">
              <Check size={20} className="text-primary mt-0.5 shrink-0" weight="bold" />
              <div>
                <p className="text-sm font-medium">Create accounts for new suppliers</p>
                <p className="text-xs text-muted-foreground">Generate temporary passwords for suppliers without accounts</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check size={20} className="text-primary mt-0.5 shrink-0" weight="bold" />
              <div>
                <p className="text-sm font-medium">Ensure existing suppliers have access</p>
                <p className="text-xs text-muted-foreground">Verify supplier portal access for users who already have accounts</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check size={20} className="text-primary mt-0.5 shrink-0" weight="bold" />
              <div>
                <p className="text-sm font-medium">Provide downloadable credentials</p>
                <p className="text-xs text-muted-foreground">Export all login credentials to CSV for easy distribution</p>
              </div>
            </div>
          </div>

          <Button
            onClick={() => setShowConfirmDialog(true)}
            disabled={processing}
            size="lg"
            className="gap-2"
          >
            {processing ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Processing Suppliers...
              </>
            ) : (
              <>
                <EnvelopeSimple size={18} weight="bold" />
                Send Bulk Invitations
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warning size={20} className="text-primary" />
              Confirm Bulk Supplier Invitation
            </DialogTitle>
            <DialogDescription>
              This will process ALL suppliers in the database. New accounts will be created for suppliers who don't have login access yet.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to proceed? This action will:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
              <li>Process all suppliers across all tenants</li>
              <li>Create user accounts where needed</li>
              <li>Generate temporary passwords</li>
              <li>Prepare credentials for distribution</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkInvite}>
              Yes, Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check size={20} className="text-primary" weight="bold" />
              Bulk Invitation Complete
            </DialogTitle>
          </DialogHeader>

          {result && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                  <p className="text-2xl font-bold text-primary">{result.summary.total_suppliers}</p>
                  <p className="text-xs text-muted-foreground">Total Suppliers</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                  <p className="text-2xl font-bold text-green-600">{result.summary.new_accounts}</p>
                  <p className="text-xs text-muted-foreground">New Accounts Created</p>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <p className="text-2xl font-bold text-blue-600">{result.summary.existing_accounts}</p>
                  <p className="text-xs text-muted-foreground">Existing Accounts</p>
                </div>
                <div className="bg-muted border border-border rounded-lg p-4">
                  <p className="text-2xl font-bold">{result.summary.invitations_sent}</p>
                  <p className="text-xs text-muted-foreground">Total Processed</p>
                </div>
              </div>

              {/* Errors */}
              {result.errors && result.errors.length > 0 && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                  <p className="text-sm font-medium text-destructive mb-2">
                    {result.errors.length} Error{result.errors.length > 1 ? 's' : ''}
                  </p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {result.errors.slice(0, 5).map((err, idx) => (
                      <p key={idx} className="text-xs text-muted-foreground">
                        • {err.supplier_name || err.email}: {err.error}
                      </p>
                    ))}
                    {result.errors.length > 5 && (
                      <p className="text-xs text-muted-foreground italic">
                        ...and {result.errors.length - 5} more
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button onClick={downloadCredentials} className="flex-1 gap-2" variant="outline">
                  <Download size={16} />
                  Download All (CSV)
                </Button>
                {result.summary.new_accounts > 0 && (
                  <Button onClick={copyNewCredentials} className="flex-1 gap-2">
                    <Copy size={16} />
                    Copy New Credentials
                  </Button>
                )}
              </div>

              <p className="text-xs text-muted-foreground text-center">
                {result.summary.new_accounts > 0 
                  ? "Share the temporary passwords securely with new suppliers. They should change their password on first login."
                  : "All suppliers already have accounts. No new credentials generated."}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowResultDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
